import {
  AbTestingConfiguration,
  SelectedModuleVariants,
  TemplateVariant,
  SanitySmProduct,
  SanitySmVideoFile,
  SpaceVariant,
  VideoFileAbTestingConfig,
  VideoFileVariant,
} from '../services/sanity/types'
import { IncomingMessage, ServerResponse } from 'http'
import { getVariantIdCookie, setVariantIdCookie } from './analytics'
import { getCookieFromRequest, setCookieOnResponse } from './server-cookies'

export const pickSpaceVariant = (variantA: SpaceVariant, variantB: SpaceVariant) => {
  return Math.random() * 100 < variantA.trafficAllocation ? variantA : variantB
}

export const pickTemplateVariant = (variantA: TemplateVariant, variantB: TemplateVariant) => {
  return Math.random() * 100 < variantA.trafficAllocation ? variantA : variantB
}

const setSpaceCookie = (space: SanitySmProduct, req?: IncomingMessage, res?: ServerResponse) => {
  const cookieName = space.spaceABTestingConfig?.cookieName
  const variantId = req ? getCookieFromRequest(req, cookieName || '') : getVariantIdCookie(cookieName || '')

  let data = space.spaceABTestingConfig?.spaceVariants.find(
    (variant) => variant.spaceDetails[0].key === variantId
  )?.spaceDetails
  if (!data && space.spaceABTestingConfig) {
    data = pickSpaceVariant(
      space.spaceABTestingConfig.spaceVariants[0],
      space.spaceABTestingConfig.spaceVariants[1]
    ).spaceDetails
    if (data[0].key) {
      if (res) {
        setCookieOnResponse(res, cookieName || '', data[0].key)
      } else {
        setVariantIdCookie(cookieName || '', data[0].key)
      }
    }
    return null
  }
  return data?.[0]
}

export const mapSpaceCardABTesting = (spaces: SanitySmProduct[], req?: IncomingMessage, res?: ServerResponse) => {
  return spaces.map((space) => {
    if (space.spaceABTestingConfig && space.spaceABTestingConfig.active) {
      const variant = pickSpaceVariant(
        space.spaceABTestingConfig.spaceVariants[0],
        space.spaceABTestingConfig.spaceVariants[1]
      )
      const variantDetails = variant.spaceDetails
      const variantFromCookie = setSpaceCookie(space, req, res)
      const newData = variantFromCookie ? variantFromCookie : variantDetails[0]
      const spaceABTestingConfig = {
        ...space.spaceABTestingConfig,
        variantNameSelected: variant.variantName,
      }

      return {
        ...space,
        ...newData,
        spaceABTestingConfig,
      } as SanitySmProduct
    }
    return space
  })
}

export const getSelectedTemplateVariant = (abTestingConfiguration?: AbTestingConfiguration): TemplateVariant | null => {
  if (!abTestingConfiguration?.active || !abTestingConfiguration.pageVariants?.length) return null

  const cookieName = abTestingConfiguration.cookieName || ''
  const pageTemplateId = abTestingConfiguration.pageTemplateId || ''

  // Use selectedVariantId from server (if available) - this will be the template ID
  const preferredId = (abTestingConfiguration as unknown as { selectedVariantId?: string })?.selectedVariantId
  if (preferredId) {
    const variant = abTestingConfiguration.pageVariants.find((v) =>
      v.controlVariant ? preferredId === pageTemplateId : v.variantId === preferredId
    )
    if (variant) {
      // Ensure cookie is set for future requests
      setVariantIdCookie(cookieName, preferredId)
      return variant
    }
  }

  // Use existing cookie value - cookie stores the template ID
  const variantId = getVariantIdCookie(cookieName)
  if (variantId) {
    const variant = abTestingConfiguration.pageVariants.find((v) =>
      v.controlVariant ? variantId === pageTemplateId : v.variantId === variantId
    )
    if (variant) {
      return variant
    }
  }
  return null
}

export const ensureTemplateVariantCookie = (abTestingConfiguration?: AbTestingConfiguration): void => {
  if (!abTestingConfiguration?.active || !abTestingConfiguration.pageVariants?.length) return
  const cookieName = abTestingConfiguration.cookieName || ''

  // 1. Check if cookie already exists
  const existingCookie = getVariantIdCookie(cookieName)
  if (existingCookie) {
    return
  }

  // 2. Use selectedVariantId from server (template content ID)
  const preferredId = (abTestingConfiguration as unknown as { selectedVariantId?: string })?.selectedVariantId
  if (preferredId) {
    setVariantIdCookie(cookieName, preferredId)
    return
  }
}

/**
 * Ensures module variant cookies (navbar-variant, footer-variant) are set client-side
 * if they were selected server-side. This is called from ClientPage on mount.
 */
export const ensureModuleVariantCookies = (moduleABTestingConfiguration?: SelectedModuleVariants): void => {
  if (!moduleABTestingConfiguration) {
    return
  }

  // Set navigation variant cookie if selected
  if (moduleABTestingConfiguration.navigationCookieName && moduleABTestingConfiguration.selectedNavigationVariantId) {
    const existingCookie = getVariantIdCookie(moduleABTestingConfiguration.navigationCookieName)
    if (!existingCookie) {
      setVariantIdCookie(
        moduleABTestingConfiguration.navigationCookieName,
        moduleABTestingConfiguration.selectedNavigationVariantId
      )
    }
  }
  // Set footer variant cookie if selected
  if (moduleABTestingConfiguration.footerCookieName && moduleABTestingConfiguration.selectedFooterVariantId) {
    const existingCookie = getVariantIdCookie(moduleABTestingConfiguration.footerCookieName)
    if (!existingCookie) {
      setVariantIdCookie(
        moduleABTestingConfiguration.footerCookieName,
        moduleABTestingConfiguration.selectedFooterVariantId
      )
    }
  }
}

export const pickVideoVariant = (variantA: VideoFileVariant, variantB: VideoFileVariant): VideoFileVariant => {
  return Math.random() * 100 < variantA.trafficAllocation ? variantA : variantB
}

export const getSelectedVideoVariant = (
  config: VideoFileAbTestingConfig | null | undefined,
  req?: IncomingMessage,
  res?: ServerResponse
): SanitySmVideoFile | null => {
  if (!config) return null
  if (!config.active || !config.variants?.length) {
    return config.variants?.[0]?.videoFile ?? null
  }

  const cookieName = config.cookieName || ''
  const variantId = req ? getCookieFromRequest(req, cookieName) : getVariantIdCookie(cookieName)

  if (variantId) {
    const matched = config.variants.find((v) => v.videoFile?._id === variantId)
    if (matched?.videoFile) return matched.videoFile
  }

  const picked = pickVideoVariant(config.variants[0], config.variants[1])
  const videoFile = picked.videoFile
  if (videoFile?._id) {
    if (res) {
      setCookieOnResponse(res, cookieName, videoFile._id)
    } else {
      setVariantIdCookie(cookieName, videoFile._id)
    }
  }
  return videoFile ?? null
}

export const ensureVideoVariantCookie = (config?: VideoFileAbTestingConfig): void => {
  if (!config?.active || !config.variants?.length) return
  const cookieName = config.cookieName || ''

  const existingCookie = getVariantIdCookie(cookieName)
  if (existingCookie) return

  const preferredId = (config as unknown as { selectedVariantId?: string })?.selectedVariantId
  if (preferredId) {
    setVariantIdCookie(cookieName, preferredId)
    return
  }

  const picked = pickVideoVariant(config.variants[0], config.variants[1])
  if (picked.videoFile?._id) {
    setVariantIdCookie(cookieName, picked.videoFile._id)
  }
}
